<template>
  <v-container fluid>
    <v-card outlined>
      <v-data-table
        :headers="getHeaders"
        :items="orders"
        :options.sync="options"
        :server-items-length="totalOrders"
        :footer-props="footerProps"
        :loading="loadingData"
        :loading-text="$t('order.ordersLoading')"
        :no-data-text="$t('order.noOrders')"
        :mobile-breakpoint="mobileBreakpoint"
        item-key="id"
        dense
      >
        <template v-slot:item.type="{ item }">
          <span v-if="item.type">{{ getTypeTranslation(item.type) }}</span>
          <span v-else>X</span>
        </template>

        <template v-slot:item.current_status="{ item }">
          <span>{{ getStatusTranslation(item.current_status) }}</span>
        </template>

        <template v-slot:item.date="{ item }">
          <span>{{ new Date(item.date) | moment('DD/MM/YYYY HH:mm:ss') }}</span>
        </template>

        <template v-slot:item.total_price="{ item }">
          {{ item.total_price | formatPrice(item.currency) }}
          {{ item.currency ? item.currency.code : item.currency_code }}
        </template>

        <template v-slot:item.customer="{ item }">
          <span>{{ item.customer.first_name }} {{ item.customer.last_name }}</span>
        </template>

        <template v-slot:item.source_location_id="{ item }">
          <span v-if="item.source_location_id">{{ item.source_location_id }}</span>
          <span v-else>X</span>
        </template>

        <template v-slot:item.pickup_location_id="{ item }">
          <span v-if="item.pickup_location_id">{{ item.pickup_location_id }}</span>
          <span v-else>X</span>
        </template>

        <template v-slot:item.action="{ item }">
          <v-btn :class="[!isMobile ? '' : 'pr-0']" color="primary" text @click="viewDetails(item.order_id)">
            <v-icon v-if="!isMobile && $vuetify.breakpoint.width <= 768">mdi-magnify</v-icon>
            <span v-else>{{ $t('order.viewDetails') }}</span>
          </v-btn>
        </template>

        <template v-slot:item.order_id="{ item }">
          <p :style="orderIdColumn" class="mb-0">{{ item.order_id }}</p>
        </template>

        <template v-slot:top>
          <v-toolbar flat>
            <v-row v-if="!isMobile">
              <v-col md="3" align-self="center" class="px-1">
                <v-text-field
                  v-model="searchOrderId"
                  :placeholder="$t('order.numOrder').toString()"
                  outlined
                  dense
                  clearable
                  hide-details
                  @click:clear="clearOrderSearch"
                />
              </v-col>
              <v-col md="3" align-self="center" class="px-1">
                <o-select-locations
                  v-model="selectedLocation"
                  :items="locations"
                  :multiple="false"
                  :placeholder="$t('subOrder.filterByLocation').toString()"
                  :outlined="true"
                  :clearable="true"
                  @input="locationChange"
                />
              </v-col>
              <v-col md="4" align-self="center" class="px-1">
                <date-filter
                  v-model="dates"
                  :clearable="true"
                  :hide-details="true"
                  :loading-data="loadingData"
                  :placeholder="$t('subOrder.filterByDates')"
                  text-field-append-icon="mdi-calendar"
                  @resetDate="resetDate"
                  @validateDate="validateDate"
                />
              </v-col>
              <v-col md="2" align-self="center" class="pl-1">
                <v-badge :content="nbActiveFilters" :value="nbActiveFilters" overlap>
                  <v-btn color="primary" outlined @click="displayFilters = !displayFilters">
                    <v-icon>mdi-filter-plus-outline</v-icon>
                  </v-btn>
                </v-badge>
              </v-col>
            </v-row>
            <v-spacer></v-spacer>
            <v-badge v-if="isMobile" :content="nbActiveFilters" :value="nbActiveFilters" overlap class="mr-2">
              <v-btn color="primary" outlined @click="displayFilters = !displayFilters">
                <v-icon>mdi-filter-plus-outline</v-icon>
              </v-btn>
            </v-badge>
            <v-btn
              :disabled="loadingData || totalOrders === 0"
              class="mr-1"
              color="primary"
              outlined
              @click="showChooseFieldToExport = true"
            >
              <v-icon>mdi-file-delimited</v-icon>
            </v-btn>
            <v-btn color="primary" outlined @click="reloadList()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
          <v-row v-if="isMobile" color="white" flat>
            <v-col cols="12">
              <v-text-field
                v-model="searchOrderId"
                class="mr-1 ml-1"
                :placeholder="$t('order.numOrder')"
                outlined
                dense
                clearable
                hide-details
                @click:clear="clearOrderSearch"
              />
            </v-col>
            <v-col cols="12">
              <o-select-locations
                class="mr-1 ml-1"
                v-model="selectedLocation"
                :items="locations"
                :multiple="false"
                :placeholder="$t('subOrder.filterByLocation').toString()"
                :outlined="true"
                :clearable="true"
                @input="locationChange"
              />
            </v-col>
            <v-col cols="12">
              <date-filter
                v-model="dates"
                :clearable="true"
                :hide-details="true"
                :loading-data="loadingData"
                :placeholder="$t('subOrder.filterByDates')"
                text-field-append-icon="mdi-calendar"
                @resetDate="resetDate"
                @validateDate="validateDate"
                text-field-class="mr-1 ml-1"
              />
            </v-col>
          </v-row>
        </template>
      </v-data-table>
      <filter-dialog
        :key="formFiltersKey"
        v-model="displayFilters"
        :fields="formFilters"
        :loadingData="loadingData"
        :title="$t('order.filterOrders')"
        @confirm="filter"
      ></filter-dialog>
      <export-fields-dialog
        v-model="showChooseFieldToExport"
        :fields="csvHeaderExport"
        :itemsFields="csvItemsExport"
        :csvPaymentsFields="csvPaymentsFieldsExport"
        :title="$t('exportCsv.csvName')"
        @exportCSV="exportCSV"
      />
    </v-card>
  </v-container>
</template>

<script>
  import _ from 'lodash'
  import { EventBus } from '@/event-bus'
  import DateFilter from '@/components/DateFilter'
  import { mapState } from 'vuex'
  import exportFieldsDialog from '@/components/SubOrders/exportFieldsDialog.vue'
  import notSetOptionMixin from '@/mixins/notSetOption'

  export default {
    name: 'OrdersTableAdmin',
    components: { DateFilter, exportFieldsDialog },
    mixins: [notSetOptionMixin],
    data() {
      return {
        orders: [],
        totalOrders: 0,
        loadingData: false,
        footerProps: {
          'items-per-page-options': [15, 30, 50],
          'items-per-page-text': this.$t('order.ordersPerPage'),
        },
        options: {
          itemsPerPage: 15,
          sortDesc: [], // fix the default value change in new vuetify version
        },
        filters: [],
        displayFilters: false,
        formFiltersKey: Date.now(),
        dates: [],
        previousDates: this.dates,
        locations: [],
        displayFieldSettings: null,
        componentPreviousPath: '',
        selectedLocation: null,
        mobileBreakpoint: 650,
        showChooseFieldToExport: false,
        searchOrderId: '',
      }
    },
    async mounted() {
      await this.getAllLocations()
      await this.getSettingsByCountry('FRA')
    },
    activated() {
      let previousPath = this.$store.state.previousPath.split('/')[1]
      // we reset filters if the previous page wasn't the details of an order of the table OR if vue recycled it from a different instance
      if (previousPath !== 'orders' || (previousPath === 'orders' && this.componentPreviousPath !== this.$route.path)) {
        this.formFiltersKey = Date.now()
        this.filters = []
        this.options.itemsPerPage = 15
        this.options.page = 1
        this.options.sortBy = []
        this.dates = []
        this.selectedLocation = null
        this.options.sortDesc = []
        this.searchOrderId = ''
      }
      this.orders = []
      this.list()
    },
    props: {
      status: {
        type: Array,
      },
    },
    computed: {
      ...mapState(['roles', 'isOctipasAdmin']),
      isMobile() {
        return this.$vuetify.breakpoint.width < this.mobileBreakpoint
      },
      nbActiveFilters: function () {
        const count = this.filters.filter((filter) => filter.active).length
        return this.searchOrderId && this.searchOrderId.trim() !== '' ? count + 1 : count
      },
      getHeaders() {
        let headers = []
        if (this.$vuetify.breakpoint.width < 769) {
          headers = this.headersMobile
        } else {
          headers = this.headers
        }
        return headers
      },
      carrierTypes() {
        return [
          { text: this.$t('order.homeDelivery'), value: 'home_delivery' },
          { text: this.$t('order.storeDelivery'), value: 'store_delivery' },
          { text: this.$t('order.relayPointDelivery'), value: 'relay_point_delivery' },
          this.notSetOption,
        ]
      },
      types() {
        return [
          { text: this.$t('subOrder.shipFromWarehouse'), value: 'ship_from_warehouse' },
          { text: this.$t('subOrder.clickAndCollect'), value: 'click_and_collect' },
          { text: this.$t('subOrder.inStorePickup'), value: 'in_store_pickup' },
          { text: this.$t('subOrder.shipFromStore'), value: 'ship_from_store' },
          { text: this.$t('subOrder.clickAndReserve'), value: 'click_and_reserve' },
          { text: this.$t('subOrder.interStore'), value: 'inter_store' },
          this.notSetOption,
        ]
      },
      headersMobile() {
        let headers = [
          {
            text: this.$t('order.numOrder'),
            value: 'order_id',
            sortable: false,
            filterType: 'text',
          },
          {
            text: this.$t('order.date'),
            value: 'date',
          },
        ]

        if (
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_name', this.roles[0])
        ) {
          headers.push({
            text: this.$t('order.client'),
            value: 'customer',
            filterType: 'text',
          })
        }
        headers.push(
          {
            text: this.$t('order.amount'),
            value: 'total_price',
            filterType: 'range',
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          }
        )
        return headers
      },
      headers() {
        let headers = [
          {
            text: this.$t('order.numOrder'),
            value: 'order_id',
            sortable: false,
            filterType: 'text',
          },
          {
            text: this.$t('order.date'),
            value: 'date',
          },
          {
            text: this.$t('subOrder.pickupLocation'),
            value: 'pickup_location_id',
          },
          {
            text: this.$t('order.status'),
            value: 'current_status',
            filterType: 'array',
            arrayValues: this.statusList,
          },
        ]

        if (
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_name', this.roles[0])
        ) {
          headers.push({
            text: this.$t('order.client'),
            value: 'customer',
            filterType: 'text',
          })
        }
        headers.push(
          {
            text: this.$t('order.amount'),
            value: 'total_price',
            filterType: 'range',
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          }
        )
        return headers
      },
      statusList() {
        return [
          { text: this.$t('order.waiting'), value: 'waiting' },
          { text: this.$t('order.waitingPayment'), value: 'waiting_payment' },
          { text: this.$t('order.confirmed'), value: 'confirmed' },
          { text: this.$t('order.partiallyConfirmed'), value: 'partially_confirmed' },
          { text: this.$t('order.toPickup'), value: 'to_pickup' },
          { text: this.$t('order.waitingCarrier'), value: 'waiting_carrier' },
          { text: this.$t('order.shippingInProgress'), value: 'shipping_in_progress' },
          { text: this.$t('order.partialShippingInProgress'), value: 'partial_shipping_in_progress' },
          { text: this.$t('order.delivered'), value: 'delivered' },
          { text: this.$t('order.deliveredPartially'), value: 'partially_delivered' },
          { text: this.$t('order.cancelled'), value: 'cancelled' },
          { text: this.$t('order.error'), value: 'error' },
          { text: this.$t('subOrder.waitingAllocation'), value: 'waiting_allocation' },
          { text: this.$t('order.blocked'), value: 'blocked' },
        ]
      },
      formFilters() {
        let headers = [
          {
            text: this.$t('order.numOrder'),
            value: 'order_id',
            filterType: 'text',
            initialValue: this.searchOrderId || '',
          },
          {
            text: this.$t('order.productId'),
            value: 'product_id',
            filterType: 'text',
          },
          {
            text: this.$t('order.carrierType'),
            value: 'carrier_type',
            filterType: 'array',
            arrayValues: this.carrierTypes,
          },
          {
            text: this.$t('order.type'),
            value: 'type',
            filterType: 'array',
            arrayValues: this.types,
          },
        ]

        if (
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_name', this.roles[0])
        ) {
          headers.push({
            text: this.$t('order.customer'),
            value: 'customer',
            filterType: 'text',
          })
        }

        if (
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_email', this.roles[0])
        ) {
          headers.push({
            text: this.$t('subOrder.email'),
            value: 'email',
            filterType: 'text',
          })
        }
        headers.push(
          {
            text: this.$t('order.status'),
            value: 'current_status',
            filterType: 'array',
            arrayValues: this.status
              ? this.statusList.filter((status) => this.status.includes(status.value))
              : this.statusList,
          },
          {
            text: this.$t('order.amount'),
            value: 'total_price',
            filterType: 'range',
          },
          {
            text: this.$t('order.origin'),
            value: 'origin',
            filterType: 'array',
            arrayValues: this.origin,
          },
          {
            text: this.$t('order.subChannel'),
            value: 'sub_channel',
            filterType: 'text',
          },
          {
            text: this.$t('order.originLocationId'),
            value: 'origin_location_id',
            filterType: 'array',
            arrayValues: this.filterLocations,
          }
        )
        return headers
      },
      origin() {
        return [
          { text: this.$t('order.web'), value: 'web' },
          { text: this.$t('order.shop'), value: 'shop' },
          { text: this.$t('order.marketplace'), value: 'marketplace' },
        ]
      },
      filterLocations() {
        let locations = []
        for (let item of this.locations) {
          locations.push({
            text: `${item.location_id} ${item.name}`,
            value: item.location_id,
          })
        }
        return locations
      },
      orderIdColumn() {
        if (this.$vuetify.breakpoint.width < 450) {
          return 'max-width: 150px'
        }
        if (this.$vuetify.breakpoint.width < 650) {
          return 'max-width: 250px'
        }
        if (this.$vuetify.breakpoint.width < 768) {
          return 'max-width: 100px'
        }
        if (this.$vuetify.breakpoint.width == 768) {
          return 'max-width: 140px'
        }
        if (this.$vuetify.breakpoint.width < 1264) {
          return 'max-width: 250px'
        } else {
          return 'max-width: 300px'
        }
      },
      csvHeaderExport() {
        return { text: this.$t('exportCsv.fieldsToExport'), arrayValues: this.csvHeaders }
      },
      csvHeaders() {
        let headers = [
          { text: this.$t('order.numOrder'), value: 'order_id' },
          { text: this.$t('exportCsv.type'), value: 'type' },
          { text: this.$t('exportCsv.date'), value: 'date' },
          { text: this.$t('exportCsv.cancellationDate'), value: 'cancellation_date' },
          { text: this.$t('exportCsv.originalPickupDeadline'), value: 'original_pickup_deadline' },
          { text: this.$t('exportCsv.pickupDeadline'), value: 'pickup_deadline' },
          { text: this.$t('exportCsv.pickupLocation'), value: 'pickup_location_id' },
          { text: this.$t('exportCsv.currentStatus'), value: 'current_status' },
          { text: this.$t('exportCsv.products'), value: 'products' },
          { text: this.$t('exportCsv.validatedProduct'), value: 'validated_products' },
          { text: this.$t('exportCsv.totalPrice'), value: 'total_price' },
          { text: this.$t('exportCsv.totalPriceExcludeTax'), value: 'total_price_exclude_tax' },
          { text: this.$t('exportCsv.validatedAmount'), value: 'validated_amount' },
          { text: this.$t('exportCsv.validatedAmountExcludeTax'), value: 'validated_amount_ht' },
          { text: this.$t('exportCsv.currencyCode'), value: 'currency_code' },
          { text: this.$t('exportCsv.cancelReason'), value: 'cancel_reason' },
          { text: this.$t('exportCsv.deliveryCountry'), value: 'delivery_country' },
          { text: this.$t('exportCsv.updatedAt'), value: 'updated_at' },
          { text: this.$t('exportCsv.origin'), value: 'origin' },
          { text: this.$t('exportCsv.carrierCost'), value: 'carrier_cost' },
          { text: this.$t('exportCsv.carrierTaxAmount'), value: 'carrier_tax_amount' },
          { text: this.$t('exportCsv.carrierName'), value: 'carrier_name' },
          { text: this.$t('exportCsv.carrierType'), value: 'carrier_type' },
          { text: this.$t('exportCsv.carrierCode'), value: 'carrier_code' },
          { text: this.$t('exportCsv.erpCarrierId'), value: 'erp_carrier_id' },
        ]

        if (
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_name', this.roles[0])
        ) {
          headers.push({ text: this.$t('exportCsv.customer'), value: 'customer' })
        }

        return headers
      },
      csvItemsExport() {
        return { text: this.$t('exportCsv.itemFieldsToExport'), arrayValues: this.itemsFields }
      },
      itemsFields() {
        return [
          { text: this.$t('exportCsv.productId'), value: 'product_id' },
          { text: this.$t('exportCsv.barcode'), value: 'barcode' },
          { text: this.$t('exportCsv.productSku'), value: 'product_sku' },
          { text: this.$t('exportCsv.productTitle'), value: 'product_title' },
          { text: this.$t('exportCsv.productSalePriceExcludeTax'), value: 'product_sale_price_exclude_tax' },
          { text: this.$t('exportCsv.productSalePrice'), value: 'product_sale_price' },
          { text: this.$t('exportCsv.rowTotalPrice'), value: 'row_total_price' },
          { text: this.$t('exportCsv.productDefaultPrice'), value: 'product_default_price' },
          { text: this.$t('exportCsv.rowTotalPriceExcludeTax'), value: 'row_total_price_exclude_tax' },
          { text: this.$t('exportCsv.currentStatus'), value: 'current_status_line' },
          { text: this.$t('exportCsv.validatedQuantity'), value: 'validated_quantity' },
          { text: this.$t('exportCsv.quantity'), value: 'product_quantity' },
        ]
      },
      csvPaymentsFieldsExport() {
        return { text: this.$t('exportCsv.csvPaymentsFieldsExport'), arrayValues: this.paymentsFields }
      },
      paymentsFields() {
        return [
          { text: this.$t('exportCsv.paymentMethodId'), value: 'payment_method_id' },
          { text: this.$t('exportCsv.paymentAmount'), value: 'payment_amount' },
          { text: this.$t('exportCsv.paymentReference'), value: 'payment_reference' },
          { text: this.$t('exportCsv.erpPaymentMethodId'), value: 'erp_payment_method_id' },
          { text: this.$t('exportCsv.capturedAmount'), value: 'captured_amount' },
        ]
      },
    },
    methods: {
      buildMyInit: function (pagination = true) {
        const { page, itemsPerPage, sortDesc, sortBy } = this.options
        const myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            sortBy: sortBy.length === 0 ? 'date' : sortBy,
            sortDesc: sortDesc.length === 0 ? true : sortDesc,
            itemsPerPage: false,
          },
        }
        if (pagination) {
          myInit.queryStringParameters.page = page
          myInit.queryStringParameters.itemsPerPage = itemsPerPage
        }

        if (this.selectedLocation) {
          myInit.queryStringParameters.locationId = this.selectedLocation
        }

        let filters = {}

        for (let filter of this.filters.filter(this.getFiltersValuesPredicate())) {
          switch (filter.name) {
            case 'carrier_type':
              myInit.queryStringParameters.carrier_types = filter.value
              break
            case 'type':
              myInit.queryStringParameters.types = filter.value
              break
            case 'current_status':
              myInit.queryStringParameters.status = filter.value
              break
            case 'origin':
              myInit.queryStringParameters.origin = filter.value
              break
            case 'origin_location_id':
              myInit.queryStringParameters[filter.name] = filter.value
              break
            default:
              filters[filter.name] = filter.value
              break
          }
        }

        if (!myInit.queryStringParameters.status && this.status) {
          myInit.queryStringParameters.status = this.status
        }
        if (this.dates.length) {
          let start = new Date(this.dates[0])
          let end = new Date()
          if (this.dates.length > 1) {
            end = new Date(this.dates[1])
          }
          filters.date = { from: start, to: end }
          if (start > end) {
            filters.date = { from: end, to: start }
          }
        }
        if (this.searchOrderId && this.searchOrderId.trim() !== '') {
          filters['order_id'] = this.searchOrderId.trim()
        }
        if (Object.entries(filters).length !== 0) {
          myInit.queryStringParameters.filters = JSON.stringify(filters)
        }
        return myInit
      },
      list() {
        this.loadingData = true
        let myInit = this.buildMyInit(true)
        this.$Amplify.API.get('orders', '/v1/int/orders', myInit)
          .then((res) => {
            this.orders = res.data
            // /v1/int/orders returns nbOrders since RTLOMS-603; nbSubOrders remains on the segment-mode empty response.
            this.totalOrders = res.nbOrders ?? res.nbSubOrders ?? 0
            this.loadingData = false
            if (this.totalOrders < (this.options.page - 1) * this.options.itemsPerPage + 1) {
              let pageNumber = Math.floor(this.totalOrders / this.options.itemsPerPage)
              this.options.page = pageNumber === 0 ? 1 : pageNumber
            }
          })
          .catch((e) => {
            this.loadingData = false
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: 'error during orders loading',
              color: 'error',
            })
          })
      },
      viewDetails(orderId) {
        this.$router.push('/orders/' + orderId)
      },
      reloadList() {
        EventBus.$emit('reLoadCounter')
        this.list()
      },
      filter(filters) {
        const orderIdFilter = filters.find((f) => f.name === 'order_id')
        if (orderIdFilter) {
          this.searchOrderId = orderIdFilter.active ? orderIdFilter.value : ''
          orderIdFilter.active = false
        }
        this.filters = filters
        this.reloadList()
      },
      getAllLocations: async function () {
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            itemsPerPage: false,
            fields: ['location_id', 'name'],
          },
        }
        this.$Amplify.API.get('locations', '/v1/int/locations', myInit)
          .then((locations) => {
            this.locations = locations.data
            this.formFiltersKey = Date.now()
          })
          .catch((e) => {
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('orderWorkflow.locationsLoadingErrorMessage'),
              color: 'error',
            })
          })
      },
      locationChange() {
        this.reloadList()
      },
      clearOrderSearch() {
        this.searchOrderId = ''
        this.reloadList()
      },
      getStatusTranslation: function (statusValue) {
        let status = this.statusList.find((status) => status.value == statusValue)
        if (status) {
          return status.text
        } else return statusValue
      },
      getTypeTranslation(typeValue) {
        const type = this.types.find((t) => t.value === typeValue)
        return type ? type.text : typeValue
      },
      validateDate() {
        if (this.dates.length > 0 && this.dates != this.previousDates) {
          this.previousDates = this.dates
          this.reloadList()
        }
      },
      resetDate() {
        this.dates = []
        this.previousDates = this.dates
        this.reloadList()
      },
      getSettingsByCountry(countryIso3) {
        // get the settings of the country selected
        const myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }

        this.$Amplify.API.get('settings', '/v1/settings/' + countryIso3, myInit)
          .then((res) => {
            this.displayFieldSettings = res.display?.customer_information
            this.formFiltersKey = Date.now()
          })
          .catch((e) => {
            console.log(e)
          })
      },
      exportCSV(value) {
        EventBus.$emit('displaySnackbar', {
          display: true,
          text: this.$t('order.CSVGenerationInProgress'),
          color: 'success',
        })
        /* get order items without pagination */
        this.loadingData = true
        let myInit = this.buildMyInit(false)

        myInit.queryStringParameters.csvHeaders = JSON.stringify(value.csvHeadersToExport)
        myInit.queryStringParameters.exportItemsLine = value.exportItemsLine
        myInit.queryStringParameters.exportCustomFields = value.exportCustomFields
        myInit.queryStringParameters.exportCustomItemFields = value.exportCustomItemFields
        let vm = this
        this.$Amplify.API.get('orders', '/v1/int/orders/export', myInit)
          .then(function (res) {
            if (!res.job_id) {
              vm.loadingData = false
              return EventBus.$emit('displaySnackbar', {
                display: true,
                text: this.$t('subOrder.jobLoadingErrorMessage'),
                color: 'error',
              })
            }
            vm.pollJob = setInterval(function () {
              vm.$Amplify.API.get('orders', '/v1/int/jobs/' + res.job_id, {
                headers: { Authorization: 'Bearer ' + vm.$auth.accessToken },
              })
                .then(function (res) {
                  if (res.status === 'error') {
                    console.log(res.error_message)
                    vm.loadingData = false
                    clearInterval(vm.pollJob)
                    return EventBus.$emit('displaySnackbar', {
                      display: true,
                      text: vm.$t('subOrder.jobLoadingErrorMessage'),
                      color: 'error',
                    })
                  }
                  let url = res.file_archive_url
                  if (url) {
                    //we create an <a> element and we simulate a click on it to trigger the downloading
                    const link = document.createElement('a')
                    link.href = url
                    document.body.appendChild(link)
                    link.click()
                    clearInterval(vm.pollJob)
                    vm.loadingData = false
                  }
                })
                .catch(function (e) {
                  console.log(e)
                  clearInterval(this.pollJob)
                  vm.loadingData = false
                  EventBus.$emit('displaySnackbar', {
                    display: true,
                    text: vm.$t('subOrder.jobLoadingErrorMessage'),
                    color: 'error',
                  })
                })
            }, 750)
          })
          .catch(function (e) {
            console.log(e)
            vm.loadingData = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: vm.$t('subOrder.jobCreationErrorMessage'),
              color: 'error',
            })
          })
      },
      getFiltersValuesPredicate() {
        return (filter) => {
          let valueIsValid = true
          if (filter?.value === null) {
            // prevents null value to crash Object.keys(f?.value).length as typeof null is "object"
            valueIsValid = false
          } else if (Array.isArray(filter?.value)) {
            valueIsValid = ![null, undefined, 0].includes(filter?.value?.length)
          } else if (typeof filter?.value === 'object') {
            valueIsValid = Object.keys(filter?.value).length
          }
          return filter?.active && valueIsValid
        }
      },
    },
    watch: {
      searchOrderId: _.debounce(function () {
        this.reloadList()
      }, 500),
      options: {
        handler() {
          this.list()
        },
      },
      $props: {
        // any change on props reload datas, use separate watcher for each prop did not work (double event)
        handler() {
          this.filters = []
          this.formFiltersKey = Date.now()
          this.list()
        },
        deep: true,
        immediate: false,
      },
    },
    // we save the current path, to have it when this component will be reused later
    beforeRouteLeave(to, from, next) {
      this.componentPreviousPath = from.path
      next()
    },
  }
</script>
